/**
 * Rolled Steel Bulb Angle 3D Mesh Component
 * 
 * Creates a precise 3D visualization of rolled steel bulb angle sections (ISBA)
 * with bulb at the top of the web, fillet radii, and principal axis rotation
 * 
 * Reference: ROLLED STEEL BULB ANGLES dimensions and properties.csv
 * 
 * Cross-section geometry (looking along beam):
 * - Bulb (semicircular) at top of web
 * - Vertical web extending downward
 * - Horizontal flange extending perpendicular
 * - Three fillet radii: r1 (root), r2 (toe), r3 (internal)
 * - Asymmetric section with tanAlpha rotation
 */

import React from 'react';
import { RolledSteelBulbAngleSection } from '../../types/beamSection';
import * as THREE from 'three';

interface RolledSteelBulbAngleMeshProps {
  section: RolledSteelBulbAngleSection;
  color?: string;
}

export const RolledSteelBulbAngleMesh: React.FC<RolledSteelBulbAngleMeshProps> = ({ 
  section, 
  color = '#00CED1' // Cyan/Turquoise for bulb angles
}) => {
  // Create the bulb angle cross-section geometry
  const geometry = React.useMemo(() => {
    const { h, b, tw, tf, D, r1, r2, r3, tanAlpha, length } = section;
    
    // Scale factor: mm to scene units (match other angle sections)
    const scale = 0.02;
    
    // Convert dimensions to scene units
    const height = h * scale;        // Overall depth
    const width = b * scale;         // Flange width
    const webThickness = tw * scale;
    const flangeThickness = tf * scale;
    const bulbDiameter = D * scale;
    const rootRadius = r1 * scale;
    const toeRadius = r2 * scale;
    const internalRadius = r3 * scale;
    
    // Calculate principal axis rotation angle
    const alpha = Math.atan(tanAlpha);
    
    console.log('Bulb Angle Debug:', {
      designation: section.designation,
      h, b, tw, tf, D,
      r1, r2, r3,
      tanAlpha,
      alphaRad: alpha,
      alphaDeg: (alpha * 180 / Math.PI).toFixed(2)
    });
    
    /**
     * Create bulb angle cross-section shape
     * 
     * IMPORTANT: THREE.Shape draws in XY plane, ExtrudeGeometry extrudes along Z
     * Following same pattern as other sections:
     * 1. Draw bulb angle shape in XY plane
     * 2. Extrude along Z for beam length
     * 3. Rotate -90° around X-axis to make Z-axis align with Y-axis
     * 4. Result: Extrusion is now along Y-axis (matches CylinderGeometry)
     */
    const shape = new THREE.Shape();
    
    // Starting point: bottom-left corner of flange (origin)
    shape.moveTo(0, 0);
    
    // TODO: Implement filleted corner using r3 (internal fillet radius)
    // Currently draws sharp corner - should use arc segments for realistic geometry
    
    // 1. Draw outer flange edge (left to right)
    // Bottom edge of flange with toe radius r2 at the tip
    shape.lineTo(width - toeRadius, 0);
    
    // Toe radius at flange tip (r2)
    if (toeRadius > 0) {
      const centerX = width - toeRadius;
      const centerY = toeRadius;
      shape.absarc(centerX, centerY, toeRadius, -Math.PI / 2, 0, false);
    }
    
    // 2. Right edge of flange (going up)
    shape.lineTo(width, flangeThickness - toeRadius);
    
    // Top corner of flange with toe radius
    if (toeRadius > 0) {
      const centerX = width - toeRadius;
      const centerY = flangeThickness - toeRadius;
      shape.absarc(centerX, centerY, toeRadius, 0, Math.PI / 2, false);
    }
    
    // 3. Top edge of flange (right to left) - connects to web
    shape.lineTo(webThickness + internalRadius, flangeThickness);
    
    // Internal fillet r3 connecting flange to web
    if (internalRadius > 0) {
      const centerX = webThickness + internalRadius;
      const centerY = flangeThickness + internalRadius;
      shape.absarc(centerX, centerY, internalRadius, -Math.PI / 2, Math.PI, true);
    }
    
    // 4. Web inner edge (going up from flange to bulb base)
    const webInnerEdge = webThickness;
    const bulbBaseHeight = height - bulbDiameter / 2; // Where bulb starts
    
    shape.lineTo(webInnerEdge, bulbBaseHeight);
    
    // 5. Bulb at top of web (semicircular)
    // The bulb is a semicircle extending outward from the web
    const bulbCenterX = webInnerEdge + bulbDiameter / 2;
    const bulbCenterY = bulbBaseHeight + bulbDiameter / 2;
    
    // Draw semicircular bulb arc from web inner edge
    if (bulbDiameter > 0) {
      shape.absarc(bulbCenterX, bulbCenterY, bulbDiameter / 2, Math.PI, 0, true);
    }
    
    // 6. Transition from bulb to outer web edge
    const webOuterEdge = webThickness + webThickness; // Outer edge of web
    
    // From bulb tip, go down along outer web line
    shape.lineTo(webOuterEdge, bulbBaseHeight);
    
    // 7. Root radius r1 where web meets flange (outer corner)
    if (rootRadius > 0) {
      const cornerHeight = flangeThickness + rootRadius;
      shape.lineTo(webOuterEdge, cornerHeight);
      
      const centerX = webOuterEdge - rootRadius;
      const centerY = flangeThickness + rootRadius;
      shape.absarc(centerX, centerY, rootRadius, 0, -Math.PI / 2, true);
    }
    
    // 8. Close the shape - outer web edge to starting point
    shape.lineTo(0, flangeThickness);
    shape.lineTo(0, 0);
    
    // Extrude the shape along Z-axis (default for ExtrudeGeometry)
    const extrudeSettings: THREE.ExtrudeGeometryOptions = {
      depth: length,  // Extrudes along Z from 0 to +length
      bevelEnabled: false,
      steps: 1,
    };
    
    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    
    // Now geometry is:
    // - Cross-section in XY plane
    // - Extruded along Z from 0 to +length
    
    // Rotate -90° around X-axis: converts Z-extrusion to Y-extrusion
    geometry.rotateX(-Math.PI / 2);
    
    // After rotation:
    // - Cross-section is now in XZ plane
    // - Extrusion is now along Y-axis from 0 to +length
    
    // Center along Y-axis (same as CylinderGeometry)
    geometry.translate(0, -length / 2, 0);
    
    // Optional: Apply principal axis rotation if needed
    // if (Math.abs(alpha) > 0.01) {
    //   geometry.rotateY(alpha);
    // }
    
    return geometry;
  }, [section]);

  return (
    <mesh geometry={geometry}>
      <meshStandardMaterial 
        color={color} 
        metalness={0.6} 
        roughness={0.4}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
};
